/**
 * Google Analytics Loader - RGPD Compliant
 * 
 * This module loads Google Analytics ONLY after explicit user consent.
 * The GA script is NOT loaded until consent is granted, ensuring no cookies
 * are set before user approval.
 * 
 * IMPORTANT: This function must ONLY be called after user explicitly accepts
 * analytics cookies in the cookie consent banner.
 */

// Declare global types for gtag
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// Get GA Measurement ID from environment variables, fallback to production ID
const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID ||
  import.meta.env.VITE_PUBLIC_GA_MEASUREMENT_ID ||
  import.meta.env.VITE_APP_GA_MEASUREMENT_ID ||
  'G-SVLGQM6J5M';

// Track if GA has been loaded to prevent double loading
let gaLoaded = false;
let isLoadingGA = false;

/**
 * Loads Google Analytics dynamically after user consent
 * 
 * This function:
 * 1. Creates and appends the gtag.js script tag dynamically
 * 2. Initializes dataLayer and gtag function
 * 3. Configures GA with measurement ID
 * 4. Ensures no cookies are set before consent
 * 
 * @returns Promise that resolves when GA is fully loaded and initialized
 */
export const loadGA = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      console.warn('[GA Loader] Window is not available');
      resolve();
      return;
    }

    // Prevent double loading
    if (gaLoaded) {
      console.log('[GA Loader] ✅ Google Analytics already loaded');
      resolve();
      return;
    }

    if (isLoadingGA) {
      console.log('[GA Loader] ⏳ Google Analytics is already loading...');
      // Wait for existing load to complete
      const checkInterval = setInterval(() => {
        if (gaLoaded) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
      return;
    }

    isLoadingGA = true;
    console.log('[GA Loader] 🚀 Starting Google Analytics load (RGPD compliant)...');

    // Initialize dataLayer BEFORE loading script
    window.dataLayer = window.dataLayer || [];

    // Define gtag function BEFORE loading script (will be replaced by real one)
    if (typeof window.gtag !== 'function') {
      window.gtag = function(...args: unknown[]) {
        window.dataLayer.push(args);
      };
    }

    // Set the current date
    window.gtag('js', new Date());

    // Create and append the script tag dynamically
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    
    script.onload = () => {
      console.log('[GA Loader] ✅ Google Analytics script loaded');
      
      // Wait for the external script to fully initialize and replace our local gtag function
      // The external script will replace window.gtag with the real implementation
      const checkGAReady = (attempts = 0) => {
        const maxAttempts = 20; // 2 seconds max (20 * 100ms)
        
        // Check if the real gtag function is loaded (not just our placeholder)
        // The real gtag function will have different behavior - it actually sends requests
        // We check by verifying the script is loaded and gtag is a function
        const scriptTag = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
        const hasRealGtag = (
          typeof window.gtag === 'function' &&
          scriptTag !== null &&
          // Additional check: the real gtag function should be different from our placeholder
          // We can verify by checking if calling gtag actually processes the dataLayer
          window.dataLayer !== undefined
        );

        if (hasRealGtag || attempts >= maxAttempts) {
          try {
            // Configure Google Analytics with measurement ID
            // send_page_view is set to false because we handle page views manually in SPA
            window.gtag('config', GA_MEASUREMENT_ID, {
              send_page_view: false,
              anonymize_ip: true,
            });

            gaLoaded = true;
            isLoadingGA = false;

            console.log('[GA Loader] ✅ Google Analytics initialized successfully');
            console.log('[GA Loader] Measurement ID:', GA_MEASUREMENT_ID);
            console.log('[GA Loader] dataLayer length:', window.dataLayer.length);
            console.log('[GA Loader] gtag type:', typeof window.gtag);
            console.log('[GA Loader] Script in DOM:', !!scriptTag);
            console.log('[GA Loader] 🍪 No cookies set before consent - RGPD compliant');

            resolve();
          } catch (error) {
            isLoadingGA = false;
            console.error('[GA Loader] ❌ Error configuring Google Analytics:', error);
            reject(error);
          }
        } else {
          // Retry after 100ms
          setTimeout(() => checkGAReady(attempts + 1), 100);
        }
      };

      // Start checking after a short delay
      setTimeout(() => checkGAReady(), 200);
    };

    script.onerror = () => {
      isLoadingGA = false;
      const errorMsg = '[GA Loader] ❌ Failed to load Google Analytics script';
      console.error(errorMsg);
      reject(new Error(errorMsg));
    };

    // Append script to head
    document.head.appendChild(script);
    console.log('[GA Loader] 📝 Script tag appended to <head>');
  });
};

/**
 * Check if Google Analytics has been loaded
 */
export const isGALoaded = (): boolean => {
  return gaLoaded && typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Diagnostic helper function (available in production for debugging)
 * Exposes window.diagnoseGA() for debugging
 */
if (typeof window !== 'undefined') {
  (window as Window & { diagnoseGA?: () => unknown }).diagnoseGA = () => {
    const scriptTag = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
    const cookieConsent = localStorage.getItem('rakb_cookie_consent');
    const cookiePrefs = localStorage.getItem('rakb_cookie_preferences');
    let parsedPrefs = null;
    try {
      parsedPrefs = cookiePrefs ? JSON.parse(cookiePrefs) : null;
    } catch {
      // Ignore parsing errors
    }

    const diagnostics = {
      'GA Loaded (internal)': gaLoaded,
      'gtag exists': typeof window.gtag === 'function',
      'dataLayer exists': Array.isArray(window.dataLayer),
      'dataLayer length': window.dataLayer?.length || 0,
      'Measurement ID': GA_MEASUREMENT_ID,
      'Script in DOM': !!scriptTag,
      'Script src': scriptTag?.getAttribute('src') || 'NOT FOUND',
      'Cookie consent': cookieConsent || 'NOT SET',
      'Analytics enabled': parsedPrefs?.analytics || false,
      'Can push events': typeof window.gtag === 'function' && Array.isArray(window.dataLayer),
    };

    console.log('=== GOOGLE ANALYTICS DIAGNOSTIC ===');
    console.table(diagnostics);
    console.log('dataLayer:', window.dataLayer);
    console.log('gtag type:', typeof window.gtag);
    console.log('Cookie preferences:', parsedPrefs);
    console.log('===================================');

    return diagnostics;
  };

  // Also expose a test function to manually send events
  (window as Window & { testGAEvent?: () => void }).testGAEvent = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'test_event', {
        event_category: 'debug',
        event_label: 'manual_test',
        value: 1
      });
      console.log('✅ Test event sent to GA');
      console.log('Check Network tab for g/collect requests');
    } else {
      console.error('❌ gtag function not available');
    }
  };

  if (import.meta.env.DEV) {
    console.log('[GA Loader] Diagnostic helpers available:');
    console.log('  - window.diagnoseGA() - Full diagnostic');
    console.log('  - window.testGAEvent() - Send test event');
  }
}

